import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip, Glyphicon } from 'react-bootstrap';
import { isEqual, cloneDeep } from 'lodash';

import {
  SortableContainer,
  SortableElement,
  arrayMove,
} from 'react-sortable-hoc';
import _ from 'underscore';
import { humanize, capitalize, titleize } from 'underscore.string';
import moment from 'moment';
import CustomerSearch from 'components/site/customer-search';
import ErrorAlert from 'components/ui/ErrorAlert';
import EquipmentActionCreators from 'actions/EquipmentActionCreators';
import OrdersStore from 'stores/OrdersStore';
import OrdersActionCreators from 'actions/OrdersActionCreators';
import CreateOrderShipmentStore from 'stores/CreateOrderShipmentStore';
import { updateOrder } from 'utils/OrderWebAPIUtils';
import { withTranslation, Translation, useTranslation } from 'react-i18next';
import { showToast } from 'stores/ToastStore';
import { getDisplayAction } from 'utils/formatters/stops';
import { mapGenerateStop, orderHazMat } from './CreateShipment';
import { IMPORT } from '../../../config/order-types';
import Alert from '../../ui/Alert';

import EditStopLocationModal from './EditStopLocationModal';
import StagingStop from './StagingStop';
import { getMarkets } from '../booking/BookingHelper';
import { sluggify } from '../../../utils/localization/utils';

const TOOLTIPS = {
  sequence: (
    <Tooltip id="sequence-tooltip">
      <Translation>{(t) => t('STOP_SEQUENCE_TIP')}</Translation>
    </Tooltip>
  ),
  location: (
    <Tooltip id="location-tooltip">
      <Translation>{(t) => t('STOP_LOCATION_TIP')}</Translation>
    </Tooltip>
  ),
  appointment: (
    <Tooltip id="appointment-tooltip">
      <Translation>{(t) => t('STOP_APPOINTMENT_TIP')}</Translation>
    </Tooltip>
  ),
  intent: (
    <Tooltip id="intent-tooltip">
      <Translation>{(t) => t('STOP_INTENT_TIP')}</Translation>
    </Tooltip>
  ),
  route: (
    <Tooltip id="route-tooltip">
      <Translation>{(t) => t('ROUTE_SEQUENCE_TIP')}</Translation>
    </Tooltip>
  ),
  equipment: (
    <Tooltip id="equipment-tooltip">
      <Translation>{(t) => t('STOP_EQUIPMENT_TIP')}</Translation>
    </Tooltip>
  ),
  carrier: (
    <Tooltip id="carrier-tooltip">
      <Translation>{(t) => t('ASSIGNED_CARRIER_TIP')}</Translation>
    </Tooltip>
  ),
};

const getInitialRefNums = (shipment) => {
  if (!_.isEmpty(shipment.reference_numbers)) {
    return shipment.reference_numbers;
  }
  return [{ name: '', value: '' }];
};

const addReferenceNumber = () => {
  return {
    name: '',
    value: '',
  };
};

const AddStop = (props) => {
  const { t } = useTranslation();
  return (
    <div className="w-full">
      <button
        className="button button-hollow-green bg-white sm:w-1/3 w-1/2 flex flex-row justify-center"
        onClick={props.addStop}
      >
        <i className="mr-2 fa fa-plus" />
        {t('STOP', { count: 1 })}
      </button>
    </div>
  );
};

AddStop.propTypes = {
  addStop: PropTypes.func.isRequired,
};

const validateShipmentType = (shipment_type, t) => {
  const errors = {};
  if (!shipment_type || shipment_type === '') {
    errors.action = t('SPECIFY_SHIPMENT_TYPE');
  }

  return errors;
};

function validateStopActions(stops, t) {
  const actions = stops.map((s) => s.action);
  const errors = {};
  if (!actions.includes('pickup')) {
    errors.action = t('PICKUP_STOP_NEEDED');
    return errors;
  }

  if (!actions.includes('deliver')) {
    errors.action = t('DELIVERY_STOP_NEEDED');
    return errors;
  }

  return errors;
}

function validateStopWindowChron(stop, t) {
  const error = {};
  if (moment(stop.window_start).isAfter(stop.window_end)) {
    error.window = t('START_END_STOP_ERROR');
  }
  return error;
}

function validateStopSequence(stop1, stop2, t) {
  const error = {};
  if (moment(stop1.window_start).isAfter(stop2.window_end)) {
    error.window = t('STOP_WINDOW_OUT_OF_SEQUENCE');
  }
  return error;
}

function validateStopWindowOverlap(stop1, stop2, t) {
  const error = {};
  if (
    (moment(stop2.window_end).isSameOrAfter(stop1.window_start) &&
      moment(stop2.window_end).isSameOrBefore(stop1.window_end)) ||
    (moment(stop2.window_start).isSameOrAfter(stop1.window_start) &&
      moment(stop2.window_start).isSameOrBefore(stop1.window_end))
  ) {
    error.window = t('STOP_WINDOW_OVERLAP_ERROR');
  }
  return error;
}

function validateWindowError(stops, t) {
  for (let i = 0; i < stops.length; i += 1) {
    const stop = stops[i];

    let error = {};

    if (stop.window_start && stop.window_end) {
      error = validateStopWindowChron(stop, t);
      if (error.window) {
        return error;
      }
    }

    if (i < stops.length - 1) {
      const stop2 = stops[i + 1];

      error = validateStopSequence(stop, stop2, t);
      if (error.window) return error;

      if (!stop.appointment) return {};

      error = validateStopWindowOverlap(stop, stop2, t);
      if (error.window) {
        return error;
      }
    }
  }
  return {};
}

const shipmentHasEquipment = (stops, equipmentId) => {
  for (let i = 0; i < stops.length; i += 1) {
    const stop = stops[i];
    if (_.indexOf(stop.equipments || [], equipmentId) >= 0) {
      return true;
    }
  }
  return false;
};

const SortableStop = SortableElement((props) => {
  const { stops, idx, setUpdatedStops } = props;

  return (
    <StagingStop
      onEdit={(updatedStop) => {
        updatedStop.sequence = idx + 1;
        const updatedStops = [...stops];
        updatedStops[idx] = updatedStop;
        setUpdatedStops(updatedStops);
      }}
      {...props}
    />
  );
});

const SortableStopsList = SortableContainer((props) => {
  const { t } = useTranslation();
  const { stops, openEditStopLocationModal, selected_timezone } = props;
  let timezone;
  if (props.order && props.order.originating) {
    timezone = props.order.originating[0]
      ? props.order.originating[0].market.timezone
      : 'America/Los_Angeles';
  } else {
    timezone = 'America/Los_Angeles';
  }
  return (
    <div className="mb-4">
      <div className="flex bg-white text-grey-darker border-solid border-b border-grey-light py-4">
        <div className="pl-2" style={{ width: '10%' }}>
          <span className="font-semibold">{t('SEQUENCE')}</span>
          <OverlayTrigger placement="top" overlay={TOOLTIPS.sequence}>
            <Glyphicon glyph="info-sign" className="ml-2" />
          </OverlayTrigger>
        </div>
        <div className="" style={{ width: '10%' }}>
          <span className="font-semibold">{t('INTENT')}</span>
          <OverlayTrigger placement="top" overlay={TOOLTIPS.intent}>
            <Glyphicon glyph="info-sign" className="ml-2" />
          </OverlayTrigger>
        </div>
        <div className="w-1/4">
          <span className="font-semibold">{t('LOCATION')}</span>
          <OverlayTrigger placement="top" overlay={TOOLTIPS.location}>
            <Glyphicon glyph="info-sign" className="ml-2" />
          </OverlayTrigger>
        </div>
        <div className="w-1/6">
          <span className="font-semibold">{t('DATE_APPOINTMENT')}</span>
          <OverlayTrigger placement="top" overlay={TOOLTIPS.appointment}>
            <Glyphicon glyph="time" className="ml-2" />
          </OverlayTrigger>
        </div>
        <div className="w-1/3">
          <span className="font-semibold">{t('EQUIPMENT_DETAILS')}</span>
          <OverlayTrigger placement="top" overlay={TOOLTIPS.equipment}>
            <Glyphicon glyph="oil" className="ml-2" />
          </OverlayTrigger>
        </div>
        <div style={{ width: '2%' }}>&nbsp;</div>
      </div>
      <div className="flex flex-col w-full">
        {stops.map((stop, idx) => (
          <SortableStop
            key={`item-${idx}`}
            index={idx} // 'index' is used by sortable list library
            idx={idx}
            timezone={timezone}
            selected_timezone={selected_timezone}
            stop={stop}
            {...props}
          />
        ))}
        <AddStop addStop={openEditStopLocationModal} />
      </div>
    </div>
  );
});

const mapPropsToState = (props) => ({
  stops: props.shipment.stops,
  carrier_id: props.shipment.carrier_id,
  carrier: props.shipment.carrier,
  instructions: props.shipment.special_instructions,
  weight: props.shipment.weight || 0,
  referenceNumbers: getInitialRefNums(props.shipment),
  showCreateStopModal: false,
  users_timezone: moment.tz.guess(),
  selectedOriginTimezone: moment.tz.guess(),
  errors: {},
  shipment_type: props.shipment.shipment_type,
  receivables: props.shipment.receivables,
});

const bindGenerateFunction = (order, stops) => {
  const lastStop = _.last(stops);
  const equipment = (lastStop.equipments || [])[0];
  return mapGenerateStop(order, equipment);
};

const orderFields = [
  'arriving',
  'delivering',
  'service_type',
  'order_type',
  'movement_type',
  'earliest_return_date',
  'earliest_empty',
];

const shipmentFields = [
  'stops',
  'carrier_id',
  'carrier',
  'special_instructions',
  'weight',
  'reference_numbers',
  'receivables',
];

const isHazMatChecked = (stops) =>
  stops.every((stop) => stop.hazards && stop.hazards.length > 0);

class ManageStagingItem extends Component {
  static propTypes = {
    order: PropTypes.object,
    shipment: PropTypes.object,
    cancelEditing: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    const state = {
      ...mapPropsToState(props),
      open: false,
    };
    this.state = state;
    this.generateStop = bindGenerateFunction(props.order, state.stops);
  }

  componentDidMount() {
    this.collapseIn();
    OrdersStore.addStopEquipmentCreatedListener(
      this.handleStopEquipmentCreated,
    );
    CreateOrderShipmentStore.addChangeListener(
      this.handleCreateOrderShipmentStoreChange,
    );
  }

  componentWillReceiveProps(nextProps) {
    const currentShipment = _.pick(this.props.shipment, shipmentFields);

    const nextShipment = _.pick(nextProps.shipment, shipmentFields);

    const currentOrder = _.pick(this.props.order, orderFields);

    const nextOrder = _.pick(nextProps.order, orderFields);

    const equipmentsChanged = !isEqual(
      this.props.order.equipments,
      nextProps.order.equipments,
    );

    const orderChanged = !isEqual(currentOrder, nextOrder);
    const shipmentChanged = !isEqual(currentShipment, nextShipment);

    let state;
    if (shipmentChanged) {
      const nextState = mapPropsToState(nextProps);
      this.generateStop = bindGenerateFunction(
        nextProps.order,
        nextState.stops,
      );
      this.setState(nextState);
    }

    if (orderChanged || shipmentChanged || equipmentsChanged) {
      if (!state) {
        state = mapPropsToState(nextProps);
      }
      this.generateStop = bindGenerateFunction(nextProps.order, state.stops);
    }

    if (this.props.shipment.expanded !== nextProps.shipment.expanded) {
      this.setState({
        open: nextProps.shipment.expanded,
      });
    }
  }

  componentWillUnmount() {
    OrdersStore.removeStopEquipmentCreatedListener(
      this.handleStopEquipmentCreated,
    );
    CreateOrderShipmentStore.removeChangeListener(
      this.handleCreateOrderShipmentStoreChange,
    );
  }

  onSortEnd = ({ oldIndex, newIndex }) => {
    let stops = arrayMove(this.state.stops, oldIndex, newIndex);
    stops = stops.map((stop, idx) => {
      stop.sequence = idx + 1;
      return stop;
    });
    this.setState({
      stops,
    });
  };

  setUpdatedStops = (updatedStops) => {
    this.setState({ stops: updatedStops });
  };

  getHandleTimezoneChange(selectedTimezone) {
    const market = getMarkets()
      .filter((m) => moment.tz(m.timezone).zoneAbbr() === selectedTimezone)
      .pop();

    if (market) {
      const { timezone } = market;
      this.setState({
        selectedOriginTimezone: timezone,
      });
    }
  }

  getUsersTimezone() {
    const market = getMarkets()
      .filter(
        (m) =>
          moment.tz(m.timezone).zoneAbbr() ===
          moment.tz(moment.tz.guess()).zoneAbbr(),
      )
      .pop();
    if (market) {
      return moment.tz(moment.tz.guess()).zoneAbbr();
    }
    return 'PST'; // Default to PST if the user is elsewhere
  }

  collapseIn() {
    setTimeout(() => this.setState({ open: true }), 100);
  }

  collapseOut() {
    setTimeout(() => this.props.cancelEditing(), 100);
  }

  handleCreateStopEquipment = (sequence, equipment) => {
    const {
      shipment: { _id: stagingShipmentId },
    } = this.props;

    EquipmentActionCreators.addEquipmentForStagingStop({
      stagingShipmentId,
      sequence,
      equipment,
    });
  };

  handleSaveSuccess() {
    this.showSuccess();
    setTimeout(() => this.hideSuccess(), 2000);
  }

  handleCreateOrderShipmentStoreChange = () => {
    const result = CreateOrderShipmentStore.getContext();

    if (!result.completed) {
      return;
    }
    const { t } = this.props;
    if (result.error) {
      const serverErrorMessage = result.error;
      let message;
      if (serverErrorMessage === 'NO_DEFAULT_CARRIER_DRIVER') {
        message = t('CARRIER_HAVE_NO_DEFAULT_DRIVER');
      } else if (serverErrorMessage) {
        message = serverErrorMessage;
      } else {
        message = t('THIS_SHIPMENT_BOOKING_ERROR');
      }
      this.displayErrorLabel(message, null);
    }
  };

  validateStopLocations = (stops = []) => {
    const { t } = this.props;
    let validate = true;
    stops.forEach((stop) => {
      if (!stop.location || !stop.location.address) {
        showToast(
          t('STOP_MISSING_LOCATION', { action: getDisplayAction(stop) }),
          t('STOP_MISSING_LOCATION', { action: getDisplayAction(stop) }),
          'error',
        );
        validate = false;
      }
    });
    return validate;
  };

  handleSave = (saveAndBook) => {
    const updatedStaging = [...this.props.order.shipments_staging];
    const stagingToUpdate = _.findWhere(this.props.order.shipments_staging, {
      _id: this.props.shipment._id,
    });

    if (stagingToUpdate) {
      const idx = _.indexOf(
        this.props.order.shipments_staging,
        stagingToUpdate,
      );

      updatedStaging[idx] = {
        _id: this.props.shipment._id,
        stops: this.state.stops,
        special_instructions: this.state.instructions,
        weight: this.state.weight,
        reference_numbers: this.state.referenceNumbers,
        carrier: this.state.carrier,
        carrier_id: this.state.carrier_id,
        shipment_type: this.state.shipment_type,
        receivables: this.state.receivables,
        ...(stagingToUpdate?.empty_rail_billing
          ? { empty_rail_billing: stagingToUpdate?.empty_rail_billing }
          : {}),
        ...(stagingToUpdate?.loaded_rail_billing
          ? { loaded_rail_billing: stagingToUpdate?.loaded_rail_billing }
          : {}),
        ...(stagingToUpdate?.requested_pickup_date
          ? { requested_pickup_date: stagingToUpdate?.requested_pickup_date }
          : {}),
        ...(stagingToUpdate?.requested_delivery_appointment
          ? {
            requested_delivery_appointment:
              stagingToUpdate?.requested_delivery_appointment,
          }
          : {}),
        ...(stagingToUpdate?.must_arrive_by_date
          ? { must_arrive_by_date: stagingToUpdate?.must_arrive_by_date }
          : {}),
      };
      updateOrder(this.props.order._id, {
        shipments_staging: updatedStaging,
      }).then(
        () => {
          if (!saveAndBook) {
            this.handleSaveSuccess();
          } else {
            const validateStopsLocation = this.validateStopLocations(
              updatedStaging[idx].stops,
            );
            if (validateStopsLocation) this.handleBook();
          }
        },
        (error) => {
          this.displayErrorLabel(error, null);
        },
      );
    }
  };

  handleCancel = () => {
    //  TODO: Add prompt to save changes if user hits Cancel or tries to navigate away
    this.setState({
      errors: {},
    });
    this.collapseOut();
  };

  handleBook = () => {
    const { stops, shipment_type } = this.state;
    const { t } = this.props;
    const windowError = validateWindowError(stops, t);
    const shipmentActionError = validateStopActions(stops, t);
    const shipmentTypeError = validateShipmentType(shipment_type, t);

    const errors = {
      ...windowError,
      ...shipmentActionError,
      ...shipmentTypeError,
    };

    const {
      order: { shipper },
    } = this.props;

    const { require_tracking_id } = shipper;

    const { referenceNumbers } = this.state;

    if (
      require_tracking_id &&
      referenceNumbers.filter((r) => r.name && r.value).length === 0
    ) {
      errors.referenceNumbers = t('PROVIDE_REF_NUM_TO_SHIPPER');
    }

    if (Object.keys(errors).length > 0) {
      this.displayErrorLabel(null, { errors });
      return;
    }

    this.props.shipment.stops = stops;
    const shipmentToCreate = {
      ...this.props.shipment,
      weight: this.state.weight || 0,
      full_truckload: true,
      hold_at_dispatch: true,
      notes: this.state.instructions,
      shipment_type: this.state.shipment_type,
      order: this.props.order._id,
    };

    if (this.state.carrier) {
      shipmentToCreate.carrier = this.state.carrier_id;
    }

    if (this.props.order.shipper) {
      shipmentToCreate.shipper = this.props.order.shipper.id.toString();
    }
    const masterBol = referenceNumbers.find((r) => r.name === 'master_bol');
    if (masterBol) {
      shipmentToCreate.reference_numbers = [
        ...shipmentToCreate.reference_numbers,
        { ...masterBol },
      ];
    }

    if (this.state.receivables) {
      shipmentToCreate.receivables = this.state.receivables;
    }

    // Send the staging ID to the server so we can remove it there ...
    shipmentToCreate.staging_id = this.props.shipment._id;

    delete shipmentToCreate.carrierName;
    delete shipmentToCreate._id;

    const data = {
      shipment: shipmentToCreate,
      action: 'add',
    };

    this.setState({ isBooking: true });

    OrdersActionCreators.createOrderShipment(data);
  };

  bookingButtonDisabled() {
    return this.state.isBooking;
  }

  displayErrorLabel = (message, error) => {
    if (error) {
      this.setState(error);
    } else {
      this.setState({
        isBooking: false,
        errors: {
          message,
        },
      });
    }
  };

  handleFieldChange = (field) => (e) =>
    this.setState({ [field]: e.target.value });

  handleWeightChange = (e) => {
    const {
      target: { value },
    } = e;

    let weight = null;

    if (value !== '' && value !== null && typeof value !== 'undefined') {
      weight = parseInt(value, 10);
    }

    this.setState({ weight });
  };

  handleRemoveReferenceNumber = (i) => () => {
    const { referenceNumbers: currentReferenceNumbers } = this.state;

    const referenceNumbers = [
      ...currentReferenceNumbers.slice(0, i),
      ...currentReferenceNumbers.slice(i + 1),
    ];
    this.setState({
      referenceNumbers,
    });
  };

  handleReferenceNumberFieldChange = (i) => (field, value) => {
    const { referenceNumbers: currentReferenceNumbers } = this.state;

    const referenceNumber = { ...currentReferenceNumbers[i] };
    referenceNumber[field] = value;
    const referenceNumbers = [
      ...currentReferenceNumbers.slice(0, i),
      referenceNumber,
      ...currentReferenceNumbers.slice(i + 1),
    ];

    this.setState({
      referenceNumbers,
    });
  };

  handleAddReferenceNumber = () => {
    const { referenceNumbers: currentReferenceNumbers } = this.state;

    const referenceNumbers = [...currentReferenceNumbers, addReferenceNumber()];

    this.setState({
      referenceNumbers,
    });
  };

  hideSuccess() {
    this.setState({ showSuccess: false });
  }

  createStop = (data) => {
    const newStop = {
      ...data,
      sequence: this.state.stops.length + 1,
    };
    const updatedStops = [...this.state.stops, newStop];
    this.setState(
      {
        stops: updatedStops,
        showCreateStopModal: false,
      },
      () => {
        this.handleSave(false);
      },
    );
  };

  showSuccess() {
    this.setState({ showSuccess: true });
  }

  removeStop = (idx) => {
    const updatedSequence = this.state.stops.slice(idx + 1).map((stop) => {
      stop.sequence -= 1;
      return stop;
    });
    const stopsUpdated = [
      ...this.state.stops.slice(0, idx),
      ...updatedSequence,
    ];

    this.setState({ stops: stopsUpdated }, () => {
      this.handleSave(false);
    });
  };

  handleAddCarrier = (item, value) => {
    this.setState({
      carrier_id: value._id,
      carrier: value.name,
    });
  };

  handleRemoveCarrier = () => {
    this.setState({
      carrier_id: null,
      carrier: null,
    });
  };

  handleShipmentTypeSelect = (shipment_type) => () => {
    this.setState((prevState) => {
      const { stops: currentStops } = prevState;

      const lastStop = _.last(currentStops);
      if (
        shipment_type === 'rail' &&
        currentStops.length > 2 &&
        lastStop.empty
      ) {
        return {
          shipment_type,
          stops: [...currentStops.slice(0, 2)],
        };
      }
      if (shipment_type === 'drayage' && currentStops.length === 2) {
        return {
          shipment_type,
          stops: [...currentStops, this.generateStop(3)],
        };
      }
      return {
        shipment_type,
      };
    });
  };

  openEditStopLocationModal = () => {
    this.setState({ showCreateStopModal: true });
  };

  handleStopEquipmentCreated = (context) => {
    const { stagingShipmentId, sequence, equipment } = context;

    const {
      shipment: { _id },
    } = this.props;

    if (_id !== stagingShipmentId) {
      return;
    }

    this.setState((prevState) => {
      const { stops: currentStops } = prevState;

      const stopIndex = currentStops.findIndex((s) => s.sequence === sequence);
      const stop = cloneDeep(currentStops[stopIndex]);

      stop.equipments = [...stop.equipments, equipment];

      const stops = [
        ...currentStops.slice(0, stopIndex),
        stop,
        ...currentStops.slice(stopIndex + 1),
      ];
      return {
        stops,
      };
    });
  };

  handleStopEdit = (idx) => (updatedStop, equipmentToEdit) => {
    const updatedStops = this.state.stops.slice();
    updatedStops[idx] = updatedStop;

    // if there's new equipment on a Stop, we need to add it to Staging Shipment if
    // it isn't there. Same with removal. If there's a more elegant solution, i'm all ears

    let equipments = this.state.equipments || [];

    if (equipmentToEdit) {
      const eq = equipmentToEdit.equipment;
      if (equipmentToEdit.message === 'add') {
        const otherStops = [
          ...updatedStops.slice(0, idx),
          ...updatedStops.slice(idx + 1),
        ];
        if (!shipmentHasEquipment(otherStops, eq._id)) {
          equipments.push(eq);
        }
      } else {
        const otherStops = [
          ...updatedStops.slice(0, idx),
          ...updatedStops.slice(idx + 1),
        ];
        if (!shipmentHasEquipment(otherStops, eq._id)) {
          const eqToRemove = _.findWhere(equipments, { _id: eq._id });
          equipments = _.without(equipments, eqToRemove);
        }
      }
    }

    this.setState({
      stops: updatedStops,
      equipments,
    });
  };

  handleHazMatChange = (e) => {
    const {
      target: { checked },
    } = e;

    this.setState((prevState) => {
      const { stops: currentStops } = prevState;
      if (!checked) {
        const stops = currentStops.map((stop) => {
          stop.hazards = [];
          return stop;
        });
        return {
          stops,
        };
      }

      const stops = currentStops.map((stop) => {
        stop.hazards = [
          {
            imo_class: this.props.order.hazmat_class,
            packing_group: this.props.order.hazmat_packaging_group,
            description: this.props.order.hazmat_description,
          },
        ];
        return stop;
      });
      return {
        stops,
      };
    });
  };

  renderTimezoneSelector() {
    const { t } = this.props;
    const TimezoneSelect = (props) => {
      return (
        <select
          value={props.selectedTimezone}
          style={{ width: '175px' }}
          className="form-select m-2 ml-0 mr-4 h-10 w-175 self-end"
          key="timezone-select"
          onChange={(e) => props.onTimezoneChange(e.target.value)}
        >
          <option value="">{t('YOUR_TIMEZONE')}</option>
          {props.timezones
            .filter((option) => typeof option === 'object')
            .map((option, key) => (
              <option value={option.abbr} key={`${option.abbr}-${key}`}>
                {option.abbr}
              </option>
            ))}
        </select>
      );
    };

    return (
      <TimezoneSelect
        selectedTimezone={
          this.state.selectedOriginTimezone
            ? moment.tz(this.state.selectedOriginTimezone).zoneAbbr()
            : this.getUsersTimezone()
        }
        onTimezoneChange={(timezone) => this.getHandleTimezoneChange(timezone)}
        timezones={getMarkets()}
      />
    );
  }

  render() {
    const { order, shipment, t } = this.props;
    const { stops, shipment_type } = this.state;

    const hazMatChecked = isHazMatChecked(stops);

    return (
      <div>
        <div className="flex flex-row justify-center">
          <section className="mb-4 p-4 bg-grey-light w-full">
            <div className="text-grey-darker">
              <span className="font-bold">{t('ROUTE_DETAILS')}</span>
              <OverlayTrigger placement="top" overlay={TOOLTIPS.route}>
                <Glyphicon glyph="map-marker" className="ml-2" />
              </OverlayTrigger>
              <div className="bg-grey-darkest mt-2 py-4 lg:px-4 flex">
                <div
                  className="p-2 bg-green items-center text-white leading-none lg:rounded-full flex lg:inline-flex w-1/2"
                  role="alert"
                >
                  <span className="flex rounded-full bg-green uppercase px-2 py-1 text-sm font-bold pr-1">
                    {t('PRO_TIP')}
                  </span>
                  <Glyphicon className="pr-2 text-xsm" glyph="pencil" />
                  <span>{t('STOP_RESEQUENCE_PRO_TIP')}</span>
                </div>
                <div className="w-1/2 flex justify-end text-white">
                  <span className="p-2 pt-1 font-semibold">{t('CARRIER')}</span>
                  <OverlayTrigger placement="top" overlay={TOOLTIPS.carrier}>
                    <Glyphicon glyph="user" className="p-1 pr-2" />
                  </OverlayTrigger>
                  <div className="w-1/2">
                    <CustomerSearch
                      clearOnSelect
                      assignField
                      placeholder={
                        this.state.carrier || t('SEARCH_FOR_CARRIERS')
                      }
                      onSelect={this.handleAddCarrier}
                      type="carriers"
                      inputStyle={{
                        height: '26px',
                        paddingLeft: '3px',
                        paddingTop: '0px',
                        paddingBottom: '0px',
                        width: '100%',
                      }}
                      displayValue={() => this.state.carrier || ''}
                    />
                  </div>
                  {this.state.carrier && (
                    <a
                      className="pt-1"
                      onClick={this.handleRemoveCarrier}
                      style={{
                        position: 'relative',
                        left: '-30px',
                      }}
                    >
                      <i className="fa fa-times-circle fa-lg cursor-pointer" />
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className="bg-white flex justify-between">
              <div>
                <InterModalButton
                  type={t('DRAYAGE')}
                  selected={shipment_type === 'drayage'}
                  handleClick={this.handleShipmentTypeSelect('drayage')}
                />
                <InterModalButton
                  type={t('RAIL')}
                  selected={shipment_type === 'rail'}
                  handleClick={this.handleShipmentTypeSelect('rail')}
                  disabled={order.mode !== 'intermodal'}
                />
              </div>
              {this.renderTimezoneSelector()}
            </div>
            {orderHazMat(order) && (
              <div className="bg-white p-2 flex text-grey-darker font-semibold">
                <input
                  type="checkbox"
                  className="my-0"
                  id="hazmat-indicator"
                  checked={hazMatChecked}
                  onChange={this.handleHazMatChange}
                />
                <label
                  className="ml-2 form-label mb-0 block cursor-pointer"
                  htmlFor="hazmat-indicator"
                >
                  {t('SHIPMENT_HAS_HAZARDOUS_MATERIALS')}
                </label>
              </div>
            )}
            <SortableStopsList
              stops={stops}
              shipment={shipment}
              order={order}
              onCreateStopEquipment={this.handleCreateStopEquipment}
              setUpdatedStops={this.setUpdatedStops}
              removeStop={this.removeStop}
              onSortEnd={this.onSortEnd}
              selected_timezone={this.state.selectedOriginTimezone}
              openEditStopLocationModal={() =>
                this.setState({ showCreateStopModal: true })
              }
              distance={1}
              helperClass="sortable-clicked"
            />

            <div className="flex">
              <div className="w-3/4">
                <ReferenceNumbers
                  referenceNumbers={this.state.referenceNumbers}
                  onRemoveReferenceNumber={this.handleRemoveReferenceNumber}
                  onReferenceNumberFieldChange={
                    this.handleReferenceNumberFieldChange
                  }
                  onAddReferenceNumber={this.handleAddReferenceNumber}
                />
              </div>
              <div className="w-1/4">
                <div className="p-4 shadow bg-white">
                  <div className="text-grey-darker border-b border-solid">
                    <span className="font-semibold">{t('LOAD_DETAILS')}</span>
                  </div>
                  <div className="p-2 pl-0 flex">
                    <label className="mb-2 form-label">
                      {titleize(t('WEIGHT'))}
                    </label>
                    <Glyphicon
                      glyph="oil"
                      className="text-grey-darker text-sm ml-1"
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      min="0"
                      step="10"
                      className="form-field block w-full"
                      value={this.state.weight || ''}
                      onChange={this.handleWeightChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <div className="mb-2 font-bold text-grey-darkest">
                {t('SPECIAL_INSTRUCTIONS')}
              </div>
              <div className="w-full flex flex-row">
                <textarea
                  style={{ resize: 'none' }}
                  className="form-field w-full"
                  value={this.state.instructions}
                  onChange={this.handleFieldChange('instructions')}
                  rows={4}
                />
                <ErrorComponent errors={this.state.errors} />
              </div>
            </div>
            <div className="flex flex-row justify-end my-4">
              {this.state.showSuccess && (
                <div className="text-green p-2">
                  {t('SHIPMENT_UPDATED_WITH_EXCLAMATION')}
                  <Glyphicon glyph="ok" className="ml-2" />
                </div>
              )}
              <button
                className="button bg-white text-black hover:bg-grey-lightest mr-2"
                onClick={() => this.handleCancel()}
              >
                {t('CANCEL')}
              </button>
              <button
                className="button bg-green hover:bg-green-dark text-white mr-2"
                onClick={() => this.handleSave(false)}
              >
                {t('SAVE')}
              </button>
              <button
                className="button bg-blue-cm hover:bg-blue-cm-darker text-white flex items-center"
                disabled={this.bookingButtonDisabled()}
                onClick={() => {
                  this.handleSave(true);
                }}
              >
                {this.state.isBooking && (
                  <i className="fa fa-circle-o-notch fa-spin" />
                )}
                {t('SAVE_AND_BOOK')}
              </button>
            </div>
          </section>
        </div>
        {this.state.showCreateStopModal && (
          <EditStopLocationModal
            onClose={() => this.setState({ showCreateStopModal: false })}
            onSave={this.createStop}
          />
        )}
      </div>
    );
  }
}

const InterModalButton = (props) => {
  const { type, selected, handleClick, disabled } = props;

  const buttonClass = selected
    ? 'button button-green m-2 ml-0 mr-4 w-32 h-10'
    : 'button button-hollow-green m-2 ml-0 mr-4 w-32 h-10 bg-white';

  const buttonStyle = selected ? { border: '1px solid white' } : {};

  return (
    <button
      className={buttonClass}
      onClick={handleClick}
      style={buttonStyle}
      disabled={disabled}
    >
      {type}
    </button>
  );
};

InterModalButton.propTypes = {
  type: PropTypes.string,
  selected: PropTypes.bool,
  handleClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const ReferenceNumbers = (props) => {
  const {
    referenceNumbers,
    onRemoveReferenceNumber,
    onReferenceNumberFieldChange,
    onAddReferenceNumber,
  } = props;
  const { t } = useTranslation();
  return (
    <div>
      <div className="m-2">
        <span className="font-semibold text-grey-darker">
          {t('REFERENCE_NUMBERS')}
        </span>
      </div>
      <div className="w-full">
        <div>
          {referenceNumbers.map((refNum, i) => (
            <ReferenceNumberField
              displayClass="reference-number-fields mb-1 md:w-3/4 px-4"
              interiorClass="reference-number-field flex flex-wrap content-center items-center w-full"
              referenceNumber={refNum}
              key={i}
              onRemove={onRemoveReferenceNumber(i)}
              onChange={onReferenceNumberFieldChange(i)}
              t={t}
            />
          ))}
        </div>
        <div className="flex w-full mt-1">
          <div className="w-full flex px-4">
            <button
              className="mb-4 bg-white button button-hollow-green sm:w-1/3 w-1/2 flex flex-row justify-center"
              onClick={onAddReferenceNumber}
            >
              <i className="mr-2 fa fa-plus" />
              <span>{t('REFERENCE_NUMBER')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

ReferenceNumbers.propTypes = {
  referenceNumbers: PropTypes.array,
  onRemoveReferenceNumber: PropTypes.func.isRequired,
  onReferenceNumberFieldChange: PropTypes.func.isRequired,
  onAddReferenceNumber: PropTypes.func.isRequired,
};

class ReferenceNumberField extends Component {
  handleNameChange = (e) => this.props.onChange('name', e.target.value);

  handleValueChange = (e) => this.props.onChange('value', e.target.value);

  handleRemove = (e) => {
    e.preventDefault();
    this.props.onRemove();
  };

  render() {
    const {
      referenceNumber: { name, value },
      t,
    } = this.props;
    return (
      <div className={this.props.displayClass || 'reference-number-fields'}>
        <div
          className={
            this.props.interiorClass ||
            'reference-number-field flex flex-wrap content-center items-center'
          }
        >
          {_.isEmpty(name) && _.isEmpty(value) ? (
            <ReferenceNumberDropdown
              name={name}
              onNameChange={this.handleNameChange}
            />
          ) : (
            <ReferenceNumberInput
              name={name}
              onNameChange={this.handleNameChange}
            />
          )}
          <div className="sm:w-2/3 w-1/2 flex items-center">
            <fieldset className="mr-2 md:w-2/3">
              <input
                type="text"
                className="block w-full mb-4 form-field"
                placeholder={t('VALUE')}
                value={value}
                onChange={this.handleValueChange}
              />
            </fieldset>
            <a href="#" className="mb-4" onClick={this.handleRemove}>
              <i className="fa fa-remove secondary-action" />
            </a>
          </div>
        </div>
      </div>
    );
  }
}

ReferenceNumberField.propTypes = {
  referenceNumber: PropTypes.object,
  interiorClass: PropTypes.string,
  displayClass: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const ReferenceNumberInput = (props) => {
  const { name, onNameChange } = props;
  const { t } = useTranslation();
  const displayName = name === 'custom' ? '' : name;
  const NON_EDITABLE = ['master_bol', 'order_id', 'delivery_order'];

  let classNames = 'w-full mb-4 block form-field';
  const disabled = NON_EDITABLE.indexOf(props.name) > -1;

  if (disabled) {
    classNames += ' bg-grey-lighter';
  }

  return (
    <div className="sm:w-1/3 w-2/5">
      <fieldset className="md:mr-2">
        <input
          disabled={disabled}
          type="text"
          className={classNames}
          placeholder={t('NAME')}
          value={displayName}
          onChange={onNameChange}
        />
      </fieldset>
    </div>
  );
};

ReferenceNumberInput.propTypes = {
  name: PropTypes.string,
  onNameChange: PropTypes.func.isRequired,
};

const availableFields = [
  {
    name: 'seal_number',
  },
  {
    name: 'carrier_invoice',
  },
];

const ReferenceNumberDropdown = ({ name, onNameChange }) => {
  const { t } = useTranslation();
  return (
    <select
      className="form-select self-start mr-4 mt-px "
      style={{ width: 'auto' }}
      value={name}
      placeholder="Select reference number type"
      onChange={onNameChange}
    >
      <option key="default-0" value="">
        {t('SELECT_A_TYPE')}
      </option>
      {availableFields.map((ref) => (
        <option key={ref.name} value={ref.name}>
          {t(sluggify(ref.name))}
        </option>
      ))}
      <option key="custom-0" value="custom">
        {t('CUSTOM')}
      </option>
    </select>
  );
};

ReferenceNumberDropdown.propTypes = {
  name: PropTypes.string,
  onNameChange: PropTypes.func.isRequired,
};

const ErrorComponent = (props) => {
  const { errors } = props;
  if (_.isEmpty(errors)) {
    return null;
  }
  const { t } = useTranslation();
  return (
    <div className="w-1/3 pl-4">
      <ErrorAlert title={t('FIX_ERROR_TO_PROCEED') + ':'}>
        <ul>
          {_.map(errors, (error, key) => (
            <li key={key}>{error}</li>
          ))}
        </ul>
      </ErrorAlert>
    </div>
  );
};

ErrorComponent.propTypes = {
  errors: PropTypes.object,
};

export default withTranslation()(ManageStagingItem);
